import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"

const QualityAnalysis = () => {
  const [loading, setLoading] = useState(true)
  const [dailyTrend, setDailyTrend] = useState([])
  const [routeQuality, setRouteQuality] = useState([])
  const [spoilage, setSpoilage] = useState([])
  const [overall, setOverall] = useState(null)

  const [from, setFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).toISOString().slice(0, 10))
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))
  const [source, setSource] = useState("vlcc")

  useEffect(() => { loadData() }, [from, to, source])

  const loadData = async () => {
    setLoading(true)
    try {
      const resp = await api.get("reports/quality-analysis", { params: { from, to, source } })
      if (resp.data.success) {
        const d = resp.data.data
        setDailyTrend(d.dailyTrend || [])
        setRouteQuality(d.routeQuality || [])
        setSpoilage(d.spoilage || [])
        setOverall(d.overall || null)
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const maxFat = Math.max(...dailyTrend.map(d => d.avgFat), 1)
  const maxSnf = Math.max(...dailyTrend.map(d => d.avgSnf), 1)

  const cardStyle = (color) => ({
    background: `linear-gradient(135deg, ${color}15, ${color}08)`, borderLeft: `4px solid ${color}`,
    borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  })

  const qualityColor = (fat) => {
    if (fat >= 5.5) return "#22c55e"
    if (fat >= 4.0) return "#f59e0b"
    return "#ef4444"
  }

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={5}><h4><i className="fas fa-flask me-2" style={{ color: "#6366f1" }}></i>Quality Analysis</h4></Col>
          <Col md={7} style={{ display: "flex", gap: "8px", justifyContent: "flex-end", alignItems: "center" }}>
            <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={from} onChange={e => setFrom(e.target.value)} />
            <span style={{ color: "#6c757d" }}>to</span>
            <input type="date" className="form-control form-control-sm" style={{ width: "140px" }} value={to} onChange={e => setTo(e.target.value)} />
            <div style={{ display: "flex", gap: "4px" }}>
              <button className={`btn btn-sm ${source === "vlcc" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setSource("vlcc")}>VLCC</button>
              <button className={`btn btn-sm ${source === "cc" ? "btn-info" : "btn-outline-info"}`} onClick={() => setSource("cc")}>CC</button>
            </div>
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {loading ? <div style={{ textAlign: "center", padding: "60px" }}><Spinner color="primary" /></div> : (
          <>
            {/* Overview Cards */}
            {overall && (
              <Row className="mb-4">
                {[
                  { label: "Average FAT", value: overall.avgFat, color: "#22c55e" },
                  { label: "Average SNF", value: overall.avgSnf, color: "#0ea5e9" },
                  { label: "Total Qty", value: Number(overall.totalQty).toLocaleString("en-IN") + " Ltrs", color: "#6366f1" },
                  { label: "Total Entries", value: overall.totalEntries?.toLocaleString("en-IN"), color: "#8b5cf6" },
                  { label: "Active Days", value: overall.daysCount, color: "#f59e0b" },
                  { label: "Spoiled Qty", value: Number(overall.spoiledQty || 0).toLocaleString("en-IN") + " Ltrs", color: "#ef4444" },
                ].map((c, i) => (
                  <Col key={i} md={2} sm={6} className="mb-3">
                    <div style={cardStyle(c.color)}>
                      <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>{c.label}</div>
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b", marginTop: "4px" }}>{c.value}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {/* FAT Trend Chart */}
            <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "16px" }}>
                <i className="fas fa-chart-line me-2"></i>Daily FAT Trend
                <span style={{ fontSize: "12px", color: "#6c757d", marginLeft: "8px" }}>({dailyTrend.length} days)</span>
              </h6>
              {dailyTrend.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>No data</div>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: "140px", overflowX: "auto" }}>
                  {dailyTrend.map((d, i) => (
                    <div key={i} style={{ minWidth: dailyTrend.length > 60 ? "6px" : "12px", flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{
                        width: "100%", maxWidth: "20px",
                        height: Math.max(4, (d.avgFat / maxFat) * 110) + "px",
                        background: qualityColor(d.avgFat),
                        borderRadius: "3px 3px 0 0", transition: "height 0.3s",
                        opacity: 0.85,
                      }} title={`${d.date}: FAT ${d.avgFat} | SNF ${d.avgSnf} | ${Number(d.totalQty).toLocaleString("en-IN")} Ltrs`}></div>
                      {i % Math.max(Math.floor(dailyTrend.length / 10), 1) === 0 && (
                        <div style={{ fontSize: "8px", color: "#94a3b8", marginTop: "3px", transform: "rotate(-45deg)", whiteSpace: "nowrap" }}>{d.date?.slice(5)}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: "16px", marginTop: "12px", fontSize: "11px" }}>
                <span><span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "2px", background: "#22c55e", marginRight: "4px" }}></span>FAT ≥ 5.5 (Excellent)</span>
                <span><span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "2px", background: "#f59e0b", marginRight: "4px" }}></span>FAT 4.0–5.5 (Good)</span>
                <span><span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "2px", background: "#ef4444", marginRight: "4px" }}></span>FAT &lt; 4.0 (Low)</span>
              </div>
            </div>

            {/* SNF Trend */}
            <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <h6 style={{ fontWeight: "700", color: "#334155", marginBottom: "16px" }}><i className="fas fa-chart-line me-2"></i>Daily SNF Trend</h6>
              {dailyTrend.length > 0 && (
                <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: "100px", overflowX: "auto" }}>
                  {dailyTrend.map((d, i) => (
                    <div key={i} style={{ minWidth: dailyTrend.length > 60 ? "6px" : "12px", flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{
                        width: "100%", maxWidth: "20px",
                        height: Math.max(4, (d.avgSnf / maxSnf) * 80) + "px",
                        background: "linear-gradient(180deg, #0ea5e9, #38bdf8)",
                        borderRadius: "3px 3px 0 0", transition: "height 0.3s", opacity: 0.85,
                      }} title={`${d.date}: SNF ${d.avgSnf}`}></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Route Quality Table */}
            <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
                <h6 style={{ fontWeight: "700", color: "#334155", margin: 0 }}><i className="fas fa-route me-2"></i>Route-wise Quality Comparison</h6>
              </div>
              {routeQuality.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>No route data</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table mb-0" size="sm">
                    <thead>
                      <tr style={{ background: "#f8fafc", fontSize: "11px", color: "#64748b", textTransform: "uppercase" }}>
                        <th>#</th><th>Route</th><th>Entries</th><th>Qty (Ltrs)</th><th>Avg FAT</th><th>Avg SNF</th><th>Quality</th>
                      </tr>
                    </thead>
                    <tbody>
                      {routeQuality.map((r, i) => (
                        <tr key={i} style={{ fontSize: "13px", borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ color: "#94a3b8" }}>{i + 1}</td>
                          <td style={{ fontWeight: "600" }}>{r.routeName}</td>
                          <td>{r.entries}</td>
                          <td style={{ color: "#0284c7" }}>{Number(r.totalQty).toLocaleString("en-IN")}</td>
                          <td><span style={{ background: "#f0fdf4", color: "#166534", padding: "2px 8px", borderRadius: "8px", fontSize: "12px", fontWeight: "600" }}>{Number(r.avgFat).toFixed(2)}</span></td>
                          <td><span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: "8px", fontSize: "12px", fontWeight: "600" }}>{Number(r.avgSnf).toFixed(2)}</span></td>
                          <td>
                            <div style={{ width: "80px", height: "8px", background: "#f1f5f9", borderRadius: "4px", overflow: "hidden" }}>
                              <div style={{ width: `${Math.min((r.avgFat / 7) * 100, 100)}%`, height: "100%", background: qualityColor(r.avgFat), transition: "width 0.5s" }}></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default QualityAnalysis
